import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, AlertTriangle, Bell } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Alerts = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    device_id: '',
    condition: 'temperature_high',
    threshold: '',
  });

  const queryClient = useQueryClient();

  // Fetch alerts
  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ['alerts'],
    queryFn: async () => {
      const response = await axios.get(`${API}/alerts`);
      return response.data;
    },
    refetchInterval: 5000,
  });

  // Fetch devices for dropdown
  const { data: devices = [] } = useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      const response = await axios.get(`${API}/devices`);
      return response.data;
    },
  });

  // Create alert mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`${API}/alerts`, {
        ...data,
        threshold: parseFloat(data.threshold),
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['alerts']);
      setIsDialogOpen(false);
      setFormData({
        name: '',
        device_id: '',
        condition: 'temperature_high',
        threshold: '',
      });
      toast.success('Alert created successfully');
    },
    onError: (error) => {
      toast.error(`Failed to create alert: ${error.message}`);
    },
  });

  // Delete alert mutation
  const deleteMutation = useMutation({
    mutationFn: async (alertId) => {
      await axios.delete(`${API}/alerts/${alertId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['alerts']);
      toast.success('Alert deleted successfully');
    },
    onError: (error) => {
      toast.error(`Failed to delete alert: ${error.message}`);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getConditionLabel = (condition) => {
    const labels = {
      temperature_high: 'Temperature High',
      temperature_low: 'Temperature Low',
      battery_low: 'Battery Low',
    };
    return labels[condition] || condition;
  };

  const getConditionColor = (condition) => {
    const colors = {
      temperature_high: 'bg-red-100 text-red-700',
      temperature_low: 'bg-blue-100 text-blue-700',
      battery_low: 'bg-orange-100 text-orange-700',
    };
    return colors[condition] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6" data-testid="alerts-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Alert Rules</h2>
          <p className="text-slate-600 mt-1">Configure temperature and battery alerts</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-alert-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Alert
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Create Alert Rule</DialogTitle>
              <DialogDescription>
                Set up a new alert rule to monitor device conditions
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="alert-form">
              <div className="space-y-2">
                <Label htmlFor="name">Alert Name</Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="alert-name-input"
                  placeholder="High Temperature Alert"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="device_id">Device</Label>
                <Select
                  name="device_id"
                  value={formData.device_id}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, device_id: value }))}
                  required
                >
                  <SelectTrigger data-testid="device-select">
                    <SelectValue placeholder="Select device" />
                  </SelectTrigger>
                  <SelectContent>
                    {devices.map((device) => (
                      <SelectItem key={device.device_id} value={device.device_id}>
                        {device.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="condition">Condition</Label>
                <Select
                  name="condition"
                  value={formData.condition}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, condition: value }))}
                >
                  <SelectTrigger data-testid="condition-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="temperature_high">Temperature High</SelectItem>
                    <SelectItem value="temperature_low">Temperature Low</SelectItem>
                    <SelectItem value="battery_low">Battery Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="threshold">Threshold</Label>
                <Input
                  id="threshold"
                  name="threshold"
                  data-testid="threshold-input"
                  type="number"
                  step="0.1"
                  placeholder={formData.condition === 'battery_low' ? '20' : '5'}
                  value={formData.threshold}
                  onChange={handleInputChange}
                  required
                />
                <p className="text-xs text-slate-500">
                  {formData.condition === 'battery_low' ? 'Alert when battery % below this value' : 'Alert when temperature °C crosses this value'}
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending}
                  data-testid="submit-alert-btn"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Alert'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Alerts Grid */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-slate-500">Loading alerts...</p>
        </div>
      ) : alerts.length === 0 ? (
        <Card>
          <CardContent className="pt-12 pb-12">
            <div className="text-center">
              <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No alerts configured</h3>
              <p className="text-slate-600 mb-6">Create your first alert rule to monitor device conditions</p>
              <Button onClick={() => setIsDialogOpen(true)} data-testid="empty-state-add-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Alert
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {alerts.map((alert) => (
            <Card key={alert.id} data-testid={`alert-${alert.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{alert.name}</CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        <span className="capitalize">{alert.status}</span>
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Device:</span>
                    <span className="text-sm font-medium text-slate-900">{alert.device_id}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Condition:</span>
                    <span className={`text-xs font-medium px-2 py-1 rounded ${getConditionColor(alert.condition)}`}>
                      {getConditionLabel(alert.condition)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Threshold:</span>
                    <span className="text-sm font-bold text-slate-900">
                      {alert.threshold}{alert.condition === 'battery_low' ? '%' : '°C'}
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <Button
                    variant="destructive"
                    size="sm"
                    className="w-full"
                    data-testid={`delete-alert-${alert.id}`}
                    onClick={() => deleteMutation.mutate(alert.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Alerts;
